import { postalCodes as postalCodesSelection, withSession } from '@zipdrug/react-api-sdk';
import { get, omit } from 'lodash';
import { graphql } from '@apollo/client/react/hoc';
import React from 'react';
import fetchOnceOptions from './utils/fetchOnceOptions';
import withActivePharmacies from './withActivePharmacies';
import withRoles from './withRoles';
import withSelectedPatientInfo from './withSelectedPatientInfo';

const withPharmaciesForPostalCodes = Component =>
  graphql(postalCodesSelection.queryPostalCodes, {
    skip: ({ userId, hasRole: { pharmacistUser, pharmacistAdmin } }) =>
      !userId || (pharmacistUser && pharmacistAdmin),
    options: ({ payer_id, postalCodes }) =>
      fetchOnceOptions('postalCodes', {
        variables: {
          query: {
            payer_id,
            postal_code: postalCodes,
          },
        },
        networkOnly: true,
      }),
    props: ({ data: { postalCodes: result } }) => ({
      pharmacyIdsForPostalCode: get(result, 'data', []).map(({ pharmacy_id }) => pharmacy_id),
    }),
  })(Component);

export default Component => {
  // Create wrapper components to replace mapProps and withProps
  const PatientInfoMapper = (props) => {
    const { selectedPatient, ...restProps } = props;
    
    const mappedProps = {
      ...restProps,
      payer_id: selectedPatient?.payer_id,
      plans: selectedPatient?.plans,
      postalCodes: selectedPatient?.addresses.map(({ postal_code }) => postal_code),
    };
    
    return <PostalCodesEnhanced {...mappedProps} />;
  };

  const ActivePharmaciesPropsMapper = (props) => {
    const { plan_id, pharmacyNetworkTypes, pharmacyIdsForPostalCode, ...restProps } = props;
    
    const queryProps = {
      ...restProps,
      query: {
        id: pharmacyIdsForPostalCode,
        status: 'active',
      },
      pharmacyNetworkTypes,
      plan_id,
    };
    
    return <ActivePharmaciesEnhanced {...queryProps} />;
  };

  const FinalPropsMapper = (props) => {
    const { activePharmacies, ...restProps } = props;
    
    const finalProps = {
      ...omit(restProps, ['query', 'language', 'postalCodes']),
      pharmaciesForPostalCodes: activePharmacies,
    };
    
    return <Component {...finalProps} />;
  };

  // Manual HOC composition (previously done by compose)
  const PostalCodesEnhanced = withPharmaciesForPostalCodes(ActivePharmaciesPropsMapper);
  const ActivePharmaciesEnhanced = withActivePharmacies(FinalPropsMapper);
  const SessionEnhanced = withSession(withRoles(withSelectedPatientInfo(PatientInfoMapper)));
  
  return SessionEnhanced;
};
